/**
 * Animation Configuration Module
 * 
 * Centralized animation configurations for Framer Motion and CSS animations.
 * Use these constants instead of defining animations inline in components.
 * 
 * This module consolidates all animation-related constants:
 * - Framer Motion variants and transitions
 * - Animation timing/duration constants
 * - Stagger delay configurations
 * - Hover effects
 * 
 * @example
 * ```tsx
 * import { 
 *   fadeInUp, 
 *   viewportSettings, 
 *   defaultTransition,
 *   ANIMATION_DURATION,
 *   ANIMATION_DELAYS 
 * } from '@/lib/animations';
 * 
 * <motion.div
 *   {...fadeInUp}
 *   viewport={viewportSettings.default}
 *   transition={defaultTransition}
 * >
 *   Content
 * </motion.div>
 * ```
 */

import type { Variants, Transition, UseInViewOptions } from "framer-motion";

// ============================================
// Duration & Timing Constants
// ============================================

/**
 * Animation durations (in milliseconds)
 * Use for CSS transitions and setTimeout
 */
export const ANIMATION_DURATION = {
  fast: 150,
  normal: 300,
  slow: 500,
} as const;

/**
 * Animation delay constants (in milliseconds)
 * Used for staggered entrance animations
 */
export const ANIMATION_DELAYS = {
  STAGGER_MS: 100,
  HERO_BADGE: 0,
  HERO_TITLE: 100,
  HERO_SUBTITLE: 200,
  HERO_BUTTONS: 300,
  HERO_SHOWCASE: 200,
  HERO_CARD_1: 400,
  HERO_CARD_2: 500,
  HERO_CARD_3: 600,
  HERO_CAROUSEL: 400,
} as const;


// ============================================
// Easing Functions
// ============================================

/**
 * Standard easing curve for most animations
 * Matches CSS ease-out-quart
 */
export const standardEase = [0.4, 0, 0.2, 1] as const;

/**
 * Smooth easing for page transitions
 */
export const smoothEase = [0.25, 0.46, 0.45, 0.94] as const;

// ============================================
// Base Animation Variants
// ============================================

/**
 * Fade in with upward movement
 * Most common animation pattern in the app
 */
export const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
} as const;

/**
 * Fade in with larger upward movement
 * Use for hero sections and emphasis
 */
export const fadeInUpLarge = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
} as const;

/**
 * Fade in with small upward movement
 * Use for subtle form field animations
 */
export const fadeInUpSmall = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
} as const;

/**
 * Simple fade in/out
 * Use for modals, overlays, and reduced motion
 */
export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
} as const;

/**
 * Fade with exit state for AnimatePresence
 */
export const fadeInOut = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
} as const;

// ============================================
// Page Transition Variants
// ============================================

/**
 * Standard page transition variants
 */
export const pageVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: smoothEase,
      when: "beforeChildren",
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.3,
      ease: smoothEase,
    },
  },
};

/**
 * Reduced motion page variants
 * Respects user's motion preferences
 */
export const reducedMotionPageVariants: Variants = {
  initial: { opacity: 0 },
  enter: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

// ============================================
// Stagger Container Variants
// ============================================

/**
 * Container for staggered children animations
 */
export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

/**
 * Child item for stagger containers
 */
export const staggerItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

// ============================================
// Viewport Settings
// ============================================

/**
 * Default viewport settings for whileInView animations
 */
export const viewportSettings = {
  /** Standard viewport trigger with margin */
  default: { once: true, margin: "-100px" } as UseInViewOptions,
  
  /** Tighter margin for earlier trigger */
  tight: { once: true, margin: "-50px" } as UseInViewOptions,
  
  /** No margin - triggers at edge */
  edge: { once: true, margin: "0px" } as UseInViewOptions,
  
  /** Repeating animation (not once) */
  repeat: { once: false, margin: "-100px" } as UseInViewOptions,
} as const;

// ============================================
// Transition Presets
// ============================================

/**
 * Default transition for most animations
 */
export const defaultTransition: Transition = {
  duration: 0.6,
  ease: standardEase,
};

/**
 * Quick transition for small UI elements
 */
export const quickTransition: Transition = {
  duration: 0.3,
  ease: standardEase,
};

/**
 * Medium transition for form fields and cards
 */
export const mediumTransition: Transition = {
  duration: 0.4,
  ease: standardEase,
};

/**
 * Slow transition for hero elements
 */
export const slowTransition: Transition = {
  duration: 0.8,
  ease: standardEase,
};

// ============================================
// Utility Functions
// ============================================

// ============================================
// Utility Functions
// ============================================

/**
 * Create a delayed transition
 * @param delay - Delay in seconds
 * @param base - Base transition to extend
 */
export const withDelay = (delay: number, base: Transition = defaultTransition): Transition => ({
  ...base,
  delay,
});

/**
 * Create staggered delay based on index
 * @param index - Item index
 * @param staggerAmount - Delay between items (default 0.1s)
 */
export const staggerDelay = (index: number, staggerAmount = 0.1): number => index * staggerAmount;

/**
 * Get animation delay respecting reduced motion preference
 * @param delay - Desired delay in seconds
 * @param shouldReduceMotion - Whether user prefers reduced motion
 */
export const getMotionDelay = (delay: number, shouldReduceMotion: boolean): number => 
  shouldReduceMotion ? 0 : delay;
